import React, { useState, useEffect } from "react";
import Layout from "../pages/Layout";
import Advertise from "../pages/Advertise";

const CropYield = () => {
  // State variables
  const [harvestedQuantity, setHarvestedQuantity] = useState(0); // Harvested quantity
  const [harvestedUnit, setHarvestedUnit] = useState("kg"); // Unit for harvested quantity
  const [areaPlanted, setAreaPlanted] = useState(0); // Area planted
  const [areaUnit, setAreaUnit] = useState("hectare"); // Unit for area planted
  const [yieldValue, setYieldValue] = useState(0); // Calculated yield

  // Conversion factors for area
  const areaConversion = {
    hectare: 1,
    acre: 2.47105,
    squareMeter: 10000,
    squareKilometer: 0.01,
  };

  // Conversion factors for harvested quantity
  const harvestConversion = {
    kg: 1,
    ton: 0.001,
    lb: 2.20462,
    gram: 1000,
  };

  // Event handlers
  const handleHarvestedQuantityChange = (e) =>
    setHarvestedQuantity(parseFloat(e.target.value) || 0);
  const handleHarvestedUnitChange = (e) => setHarvestedUnit(e.target.value);
  const handleAreaPlantedChange = (e) =>
    setAreaPlanted(parseFloat(e.target.value) || 0);
  const handleAreaUnitChange = (e) => setAreaUnit(e.target.value);

  // Calculate yield based on selected units
  useEffect(() => {
    if (areaPlanted > 0) {
      const normalizedHarvestedQuantity =
        harvestedQuantity * harvestConversion[harvestedUnit];
      const normalizedAreaPlanted =
        areaPlanted / areaConversion[areaUnit];
      const cropYield = normalizedHarvestedQuantity / normalizedAreaPlanted;
      setYieldValue(cropYield);
    }

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });

  }, [harvestedQuantity, harvestedUnit, areaPlanted, areaUnit]);

  return (
    <Layout title="Crop Yield Calculator" path="crop-yield">
      <section className="section-padding" id="crop-yield">
        <div className="container mt-5">
          <div className="row">
            {/* Crop Yield Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">Crop Yield Calculator</h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Input for Harvested Quantity */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="harvestedQuantity" className="form-label">
                        Harvested Quantity
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Harvested Quantity:</strong> The total quantity of crops harvested after the growing season, typically measured in weight."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="harvestedQuantity"
                        placeholder="Enter harvested quantity"
                        value={harvestedQuantity || ""}
                        onChange={handleHarvestedQuantityChange}
                      />
                      <select
                        className="form-select mt-2"
                        value={harvestedUnit}
                        onChange={handleHarvestedUnitChange}
                      >
                        <option value="kg">Kilograms (kg)</option>
                        <option value="ton">Tons</option>
                        <option value="lb">Pounds (lb)</option>
                        <option value="gram">Grams (g)</option>
                      </select>
                    </div>

                    {/* Input for Area Planted */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="areaPlanted" className="form-label">
                        Area Planted
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Area Planted:</strong> The total area of land used for planting crops. Typically measured in hectares, acres, or square meters."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="areaPlanted"
                        placeholder="Enter area planted"
                        value={areaPlanted || ""}
                        onChange={handleAreaPlantedChange}
                      />
                      <select
                        className="form-select mt-2"
                        value={areaUnit}
                        onChange={handleAreaUnitChange}
                      >
                        <option value="hectare">Hectares</option>
                        <option value="acre">Acres</option>
                        <option value="squareMeter">Square Meters</option>
                        <option value="squareKilometer">Square Kilometers</option>
                      </select>
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Crop Yield:{" "}
                        <span className="fw-bold ms-3">
                          {yieldValue === 0
                            ? "0"
                            : yieldValue.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}{" "}
                          units per normalized area
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to Crop Yield Calculation
                </h3>

                {/* What is Crop Yield? */}
                <h4 className="mt-3 mb-3">What is Crop Yield?</h4>
                <p>
                  **Crop Yield** refers to the productivity of a particular crop, measured as the harvested quantity per unit area planted. It is a critical metric for evaluating agricultural efficiency and productivity.
                </p>

                {/* Why Use the Crop Yield Calculator? */}
                <h4 className="mt-4 mb-3">Why Use the Crop Yield Calculator?</h4>
                <p>
                  This calculator helps farmers and agricultural planners make informed decisions by providing accurate crop yield estimates based on harvested quantity and planted area.
                </p>

                {/* Benefits of Using the Crop Yield Calculator */}
                <h4 className="mt-4 mb-3">Benefits of Using the Crop Yield Calculator</h4>
                <ul>
                  <li>
                    <strong>Aids in Planning for Future Planting Seasons:</strong> Helps allocate resources for optimal productivity.
                  </li>
                  <li>
                    <strong>Optimizes Land Use:</strong> Identifies underperforming areas and opportunities for improvement.
                  </li>
                  <li>
                    <strong>Estimates Revenue:</strong> Provides insights into potential income from the harvested crop.
                  </li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">Who Should Use the Crop Yield Calculator?</h4>
                <p>
                  This calculator is suitable for:
                </p>
                <ul>
                  <li>
                    <strong>Farmers:</strong> Evaluate and improve crop productivity.
                  </li>
                  <li>
                    <strong>Agricultural Planners:</strong> Optimize land and resource allocation.
                  </li>
                  <li>
                    <strong>Agronomists:</strong> Monitor and advise on crop yield improvements.
                  </li>
                </ul>

                {/* Question 1 */}
                <h5 className="mt-3 mb-2">How does crop yield affect farm profitability?</h5>
                <p>
                  Higher crop yields directly impact farm profitability by maximizing output relative to input costs. Efficient yield management ensures sustainable revenue generation.
                </p>

                {/* Question 2 */}
                <h5 className="mt-3 mb-2">
                  Can this calculator account for multiple crops on the same land?
                </h5>
                <p>
                  Yes, by calculating yields separately for each crop and considering their respective planted areas, you can estimate the total productivity and optimize multi-crop farming.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default CropYield;
